import { useEffect, useRef, useState, MouseEvent as ReactMouseEvent } from "react";

type ReportOption = {
  id: string;
  label: string;
  disabled?: boolean;
};

type Props = {
  reports: ReportOption[];
  value?: string;
  onChange?: (reportId: string) => void;
  className?: string;
};

export function ReportsDropdown({
  reports,
  value,
  onChange,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = reports.find((report) => report.id === value);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handlePick = (event: ReactMouseEvent, report: ReportOption) => {
    event.stopPropagation();
    if (report.disabled) return;
    onChange?.(report.id);
    setOpen(false);
  };

  return (
    <div
      className={`reports-dropdown ${className ?? ""}`}
      ref={containerRef}
      onClick={() => setOpen((o) => !o)}
    >
      <style>{`
        .reports-dropdown {
          position: relative;
          display: inline-block;
          border: 1px solid #c6cdda;
          border-radius: 4px;
        }

        .reports-dropdown:focus-within {
          border-color: #86b7fe;
        }

        .reports-dropdown-toggle {
          width: 100%;
          white-space: nowrap;
          background: transparent;
          color: inherit;
          border: none;
          padding: 0;
          text-align: left;
          cursor: pointer;
        }

        .reports-dropdown-toggle:focus {
          outline: none;
          box-shadow: none;
        }

        .reports-dropdown-menu {
          position: absolute;
          z-index: 1000;
          top: calc(100% + 2px);
          left: 0;
          min-width: 100%;
          margin: 0;
          padding: 0;
          list-style: none;
          background-color: var(--bs-white);
          border: 1px solid #c6cdda;
          border-radius: 4px;
          max-height: 300px;
          overflow-y: auto;
        }

        .reports-dropdown-menu li {
          padding: 4px 12px !important;
          margin: 0 !important;
          cursor: pointer;
          white-space: nowrap;
          background-color: var(--bs-white) !important;
        }

        .reports-dropdown-menu li:hover {
          background-color: var(--bs-gray-100) !important;
        }

        .reports-dropdown-menu li.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .reports-dropdown-menu li.disabled:hover {
          background-color: var(--bs-white) !important;
        }

        .reports-dropdown-menu li.placeholder {
          font-style: italic;
        }
      `}</style>

      <button type="button" className="reports-dropdown-toggle">
        {selected?.label ?? "Please select report"}
      </button>

      {open && (
        <ul className="reports-dropdown-menu" role="listbox">
          <li className="placeholder disabled" role="option" aria-disabled="true">
            Please select report
          </li>
          {reports.map((report) => (
            <li
              key={report.id}
              role="option"
              aria-disabled={report.disabled}
              aria-selected={report.id === value}
              className={report.disabled ? "disabled" : ""}
              onClick={(event) => handlePick(event, report)}
            >
              {report.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
